import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";
import type { ScheduleFrequency, ScheduleTriggerConfig } from "../../../api/client.ts";
import { formatDate } from "../../../utils/format.ts";
import { WEEKDAY_NAMES, WEEKDAY_SHORT, scheduleLabel, timeZones } from "../schedule.ts";

const FREQUENCIES: { value: ScheduleFrequency; label: string }[] = [
  { value: "hourly", label: "Every hour" },
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "On chosen days" },
];

/** The schedule trigger's settings: how often, at what time, in which time zone. */
export default function ScheduleFields({
  schedule,
  onChange,
  disabled,
  saved,
}: {
  schedule: ScheduleTriggerConfig;
  onChange: (schedule: ScheduleTriggerConfig) => void;
  disabled: boolean;
  // The saved workflow's schedule and next slot (null while paused); absent before the first save.
  saved?: { schedule: ScheduleTriggerConfig; nextFireAt: string | null };
}) {
  const unchanged = saved !== undefined && scheduleLabel(saved.schedule) === scheduleLabel(schedule);
  const zones = useMemo(() => timeZones(), []);
  const setFrequency = (frequency: ScheduleFrequency) => {
    const { timezone } = schedule;
    const time = schedule.time ?? "08:00";
    if (frequency === "hourly") onChange({ frequency, timezone, minute: schedule.minute ?? 0 });
    else if (frequency === "daily") onChange({ frequency, timezone, time });
    else onChange({ frequency, timezone, time, weekdays: schedule.weekdays ?? [0, 1, 2, 3, 4] });
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Runs"
        select
        size="small"
        fullWidth
        disabled={disabled}
        value={schedule.frequency}
        onChange={(e) => setFrequency(e.target.value as ScheduleFrequency)}
      >
        {FREQUENCIES.map((f) => (
          <MenuItem key={f.value} value={f.value}>
            {f.label}
          </MenuItem>
        ))}
      </TextField>
      {schedule.frequency === "weekly" && (
        <ToggleButtonGroup
          size="small"
          fullWidth
          disabled={disabled}
          value={schedule.weekdays ?? []}
          onChange={(_, days: number[]) =>
            onChange({ ...schedule, weekdays: [...days].sort((a, b) => a - b) })
          }
          aria-label="Days"
        >
          {WEEKDAY_SHORT.map((label, day) => (
            <ToggleButton key={day} value={day} aria-label={WEEKDAY_NAMES[day]}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      {schedule.frequency === "hourly" ? (
        <TextField
          label="Minute past the hour"
          type="number"
          size="small"
          fullWidth
          disabled={disabled}
          value={schedule.minute ?? 0}
          onChange={(e) =>
            onChange({ ...schedule, minute: e.target.value === "" ? 0 : Math.round(Number(e.target.value)) })
          }
          slotProps={{ htmlInput: { min: 0, max: 59, step: 1 } }}
        />
      ) : (
        <TextField
          label="Time"
          type="time"
          size="small"
          fullWidth
          disabled={disabled}
          value={schedule.time ?? ""}
          onChange={(e) => onChange({ ...schedule, time: e.target.value })}
          slotProps={{ htmlInput: { step: 60 }, inputLabel: { shrink: true } }}
        />
      )}
      <Autocomplete
        size="small"
        fullWidth
        disableClearable
        disabled={disabled}
        options={zones}
        value={schedule.timezone}
        onChange={(_, timezone) => onChange({ ...schedule, timezone })}
        renderInput={(params) => <TextField {...params} label="Time zone" />}
      />
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {scheduleLabel(schedule)}.{" "}
        {!unchanged
          ? "The next run is set when you save."
          : saved.nextFireAt
            ? `Next run ${formatDate(saved.nextFireAt, "datetime")}.`
            : "Paused: no run is due."}{" "}
        A run is skipped while the previous one is still working.
      </Typography>
    </Stack>
  );
}
